// features/procurement/CreateRequisitionPage.tsx
/**
 * Create Purchase Requisition — Redesigned with high-tier SaaS UI/UX.
 * Features:
 * - 1-Click "Scan Low Stock" Intelligence: automatically identifies items below minimum threshold
 * - Instant "Import All Low Stock Items" with optimal reorder quantities
 * - Priority status tagging (Normal, High, Critical / Urgent)
 * - Searchable product catalog with live UOM selection
 * - Real-time estimated budget valuation
 * - Zero silent failures with pre-validation and descriptive notifications
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Spinner } from '@/components/ui/Spinner';
import { getBranches } from '@/features/settings/services/settingsApi';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Plus, Save, Trash2, ClipboardList, Store,
    Search, CheckCircle2, AlertCircle, Info,
    ChevronRight, ChevronLeft, Package, Sparkles,
    Zap, AlertTriangle, X, ArrowDownRight, Layers
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    createRequisition,
    checkLowStock,
    getProcurementErrorMessage,
} from './services/procurementApi';

// ============================================================
// TYPES
// ============================================================

interface RequisitionItem {
    id: string; // local temporary key
    productId: string;
    productName: string;
    sku?: string;
    quantity: string;
    uomId: string;
    uomName?: string;
    availableUoms: Array<{ id: string; name: string; isBaseUom?: boolean }>;
    estimatedUnitCost: string;
    notes: string;
    isLowStockImported?: boolean;
    currentStock?: string;
    reorderLevel?: string;
}

export function CreateRequisitionPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [currentStep, setCurrentStep] = useState<1 | 2>(1);
    const [priority, setPriority] = useState<'normal' | 'high' | 'urgent'>('normal');
    const [form, setForm] = useState({
        branchId: selectedBranchId || '',
        notes: '',
    });

    const [items, setItems] = useState<RequisitionItem[]>([
        {
            id: 'item-1',
            productId: '',
            productName: '',
            quantity: '1',
            uomId: '',
            availableUoms: [],
            estimatedUnitCost: '',
            notes: '',
        },
    ]);

    const [activeProductDropdownIndex, setActiveProductDropdownIndex] = useState<number | null>(null);
    const [productSearch, setProductSearch] = useState('');
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [showLowStockModal, setShowLowStockModal] = useState(false);

    // ============================================================
    // QUERIES
    // ============================================================

    const { data: branchesData, isLoading: isLoadingBranches } = useQuery({
        queryKey: ['branches-req', user?.tenantId],
        queryFn: () => getBranches(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const { data: productsData } = useQuery({
        queryKey: ['products-req', user?.tenantId, productSearch],
        queryFn: async () => {
            const params: any = { limit: 50 };
            if (productSearch.trim()) params.search = productSearch.trim();
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/products`, { params });
            if (Array.isArray(data)) return data;
            if (data?.data && Array.isArray(data.data)) return data.data;
            return [];
        },
        enabled: !!user?.tenantId,
    });

    // Low stock intelligence for branch
    const { data: lowStockData, isLoading: isLoadingLowStock, refetch: refetchLowStock } = useQuery({
        queryKey: ['low-stock-req', user?.tenantId, form.branchId],
        queryFn: () => checkLowStock(user!.tenantId, form.branchId || undefined),
        enabled: !!user?.tenantId && !!form.branchId,
    });

    const branches = branchesData || [];
    const products = productsData || [];
    const lowStockItems = lowStockData?.items || [];

    // ============================================================
    // LOW STOCK 1-CLICK IMPORT
    // ============================================================

    const handleImportAllLowStock = () => {
        if (!lowStockItems || lowStockItems.length === 0) {
            addToast({
                type: 'info',
                title: 'No Low Stock Detected',
                message: 'All inventory items are currently above their reorder thresholds.',
            });
            return;
        }

        const importedItems: RequisitionItem[] = lowStockItems.map((ls: any, idx: number) => {
            const current = parseFloat(ls.currentStock || '0');
            const reorder = parseFloat(String(ls.threshold || '10'));
            const targetQty = Math.max(1, Math.ceil(reorder * 1.5 - current));

            const uoms = Array.isArray(ls.uoms) && ls.uoms.length > 0
                ? ls.uoms
                : [{ id: ls.uomId || '00000000-0000-0000-0000-000000000000', name: ls.uomName || 'Unit', isBaseUom: true }];

            const defaultUom = uoms.find((u: any) => u.isBaseUom) || uoms[0];

            return {
                id: `lowstock-${ls.productId || idx}-${Date.now()}`,
                productId: ls.productId,
                productName: ls.productName || 'Low Stock Product',
                sku: ls.sku,
                quantity: String(targetQty),
                uomId: defaultUom.id,
                uomName: defaultUom.name,
                availableUoms: uoms,
                estimatedUnitCost: ls.costPrice ? String(ls.costPrice) : '0.00',
                notes: `Auto-requisitioned: Current stock (${current}) is below threshold (${reorder})`,
                isLowStockImported: true,
                currentStock: String(current),
                reorderLevel: String(reorder),
            };
        });

        // Filter out empty starter row if only 1 empty row existed
        const hasOnlyEmptyRow = items.length === 1 && !items[0].productId;
        setItems(hasOnlyEmptyRow ? importedItems : [...items, ...importedItems]);
        setShowLowStockModal(false);

        addToast({
            type: 'success',
            title: 'Low Stock Items Imported',
            message: `Populated ${importedItems.length} replenishment item(s) into your requisition.`,
        });
    };

    // ============================================================
    // MUTATION
    // ============================================================

    const createReqMutation = useMutation({
        mutationFn: async () => {
            const errors: string[] = [];

            if (!form.branchId) errors.push('Please specify the requesting Branch facility.');

            const validItems = items.filter(i => i.productId && parseFloat(i.quantity || '0') > 0);
            if (validItems.length === 0) {
                errors.push('At least one item with valid product, quantity, and UOM is required.');
            }

            items.forEach((item, idx) => {
                if (!item.productId) {
                    errors.push(`Item #${idx + 1}: No product selected.`);
                } else {
                    const qty = parseFloat(item.quantity || '0');
                    if (qty <= 0) {
                        errors.push(`Item #${idx + 1} (${item.productName || 'Product'}): Quantity requested must be greater than 0.`);
                    }
                    if (!item.uomId) {
                        errors.push(`Item #${idx + 1} (${item.productName || 'Product'}): Unit of Measure (UOM) is required.`);
                    }
                }
            });

            if (errors.length > 0) {
                setValidationErrors(errors);
                throw new Error(errors[0]);
            }

            setValidationErrors([]);

            const finalNotes = [
                priority !== 'normal' ? `[PRIORITY: ${priority.toUpperCase()}]` : null,
                form.notes?.trim() || null,
            ].filter(Boolean).join(' ');

            return createRequisition(user!.tenantId, {
                branchId: form.branchId,
                notes: finalNotes || undefined,
                items: validItems.map(i => ({
                    productId: i.productId,
                    quantity: i.quantity,
                    uomId: i.uomId,
                    estimatedUnitCost: i.estimatedUnitCost || undefined,
                    notes: i.notes?.trim() || undefined,
                })),
            });
        },
        onSuccess: (req) => {
            addToast({
                type: 'success',
                title: 'Purchase Requisition Submitted',
                message: `Requisition #${req.requisitionNumber} has been logged for managerial review.`,
            });
            queryClient.invalidateQueries({ queryKey: ['requisitions-list'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-requisitions'] });
            navigate(`/procurement/requisitions/${req.id}`);
        },
        onError: (err: any) => {
            const message = getProcurementErrorMessage(err, err?.message || 'Failed to file purchase requisition.');
            addToast({
                type: 'error',
                title: 'Requisition Error',
                message,
            });
        },
    });

    // ============================================================
    // HANDLERS
    // ============================================================

    const addItem = () => {
        setItems(prev => [
            ...prev,
            {
                id: `req-item-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
                productId: '',
                productName: '',
                quantity: '1',
                uomId: '',
                availableUoms: [],
                estimatedUnitCost: '',
                notes: '',
            },
        ]);
    };

    const removeItem = (idx: number) => {
        if (items.length <= 1) {
            addToast({
                type: 'warning',
                title: 'Action Restricted',
                message: 'At least one requisition item row must remain.',
            });
            return;
        }
        setItems(prev => prev.filter((_, i) => i !== idx));
    };

    const updateItem = (idx: number, field: keyof RequisitionItem, value: any) => {
        setItems(prev => {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], [field]: value };
            return updated;
        });
    };

    const selectProduct = (idx: number, product: any) => {
        const uoms = Array.isArray(product.uoms) && product.uoms.length > 0
            ? product.uoms
            : [{ id: product.uomId || '00000000-0000-0000-0000-000000000000', name: product.uomName || 'Unit', isBaseUom: true }];

        const defaultUom = uoms.find((u: any) => u.isBaseUom) || uoms[0];

        setItems(prev => {
            const updated = [...prev];
            updated[idx] = {
                ...updated[idx],
                productId: product.id,
                productName: product.name,
                sku: product.sku || '',
                availableUoms: uoms,
                uomId: defaultUom?.id || '',
                uomName: defaultUom?.name || 'Unit',
                estimatedUnitCost: product.costPrice ? String(product.costPrice) : updated[idx].estimatedUnitCost || '0.00',
            };
            return updated;
        });
        setActiveProductDropdownIndex(null);
    };

    // ============================================================
    // METRICS
    // ============================================================

    const totalRequestedQty = items.reduce((sum, item) => sum + parseFloat(item.quantity || '0'), 0);
    const totalEstimatedBudget = items.reduce((sum, item) => {
        const qty = parseFloat(item.quantity || '0');
        const cost = parseFloat(item.estimatedUnitCost || '0');
        return sum + (isNaN(qty) || isNaN(cost) ? 0 : qty * cost);
    }, 0);

    const formatNaira = (val: number): string =>
        `₦${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const canProceedToItems = Boolean(form.branchId);
    const validItemsCount = items.filter(
        i => i.productId && parseFloat(i.quantity || '0') > 0 && i.uomId
    ).length;
    const isReadyToSubmit = canProceedToItems && validItemsCount > 0;

    return (
        <div className="min-h-screen bg-slate-50/60 text-slate-900 pb-20">
            {/* Top Action Bar */}
            <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/procurement/requisitions')}
                                className="p-2.5 rounded-xl bg-white hover:bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200 transition-all shadow-sm"
                                type="button"
                                title="Back to Requisitions"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg shadow-blue-500/20 text-white">
                                <ClipboardList className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">
                                        Internal Purchase Requisition
                                    </h1>
                                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200 uppercase tracking-wider">
                                        Stock Indent
                                    </span>
                                </div>
                                <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                                    <span>Branch Inventory Replenishment</span>
                                    <span>•</span>
                                    <span>{totalRequestedQty.toLocaleString()} units requested</span>
                                </p>
                            </div>
                        </div>

                        {/* Top CTAs */}
                        <div className="flex items-center gap-3 self-end sm:self-auto">
                            <button
                                onClick={() => navigate('/procurement/requisitions')}
                                className="px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold text-sm transition-all shadow-sm"
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => createReqMutation.mutate()}
                                disabled={!isReadyToSubmit || createReqMutation.isPending}
                                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all font-bold text-sm shadow-xl shadow-blue-600/25 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                                type="button"
                            >
                                {createReqMutation.isPending ? (
                                    <>
                                        <Spinner size="xs" variant="white" />
                                        <span>Submitting Indent...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        <span>Submit Requisition</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-8">
                {/* Step Indicators */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                        type="button"
                        onClick={() => setCurrentStep(1)}
                        className={clsx(
                            'p-4 rounded-2xl border text-left transition-all flex items-center justify-between',
                            currentStep === 1
                                ? 'bg-white border-blue-500 text-blue-700 shadow-sm ring-2 ring-blue-500/20'
                                : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                        )}
                    >
                        <div className="flex items-center gap-3">
                            <div className={clsx(
                                'w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm',
                                canProceedToItems ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600'
                            )}>
                                {canProceedToItems ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : '1'}
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-900">Step 1: Department & Priority</p>
                                <p className="text-xs text-slate-500">Requesting branch facility, urgency level & justification</p>
                            </div>
                        </div>
                        <ChevronRight className="w-5 h-5 opacity-40" />
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            if (canProceedToItems) setCurrentStep(2);
                            else {
                                addToast({
                                    type: 'warning',
                                    title: 'Required Details',
                                    message: 'Please select a requesting branch location first.',
                                });
                            }
                        }}
                        className={clsx(
                            'p-4 rounded-2xl border text-left transition-all flex items-center justify-between',
                            currentStep === 2
                                ? 'bg-white border-blue-500 text-blue-700 shadow-sm ring-2 ring-blue-500/20'
                                : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                        )}
                    >
                        <div className="flex items-center gap-3">
                            <div className={clsx(
                                'w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm',
                                validItemsCount > 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600'
                            )}>
                                {validItemsCount > 0 ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : '2'}
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-900">Step 2: Requisition Items & Budget</p>
                                <p className="text-xs text-slate-500">{validItemsCount} items · Estimated: {formatNaira(totalEstimatedBudget)}</p>
                            </div>
                        </div>
                        <ChevronRight className="w-5 h-5 opacity-40" />
                    </button>
                </div>

                {/* Validation Banner if errors exist */}
                {validationErrors.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-3 shadow-sm"
                    >
                        <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                        <div className="space-y-1">
                            <p className="font-bold text-rose-900">Unable to submit requisition due to incomplete fields:</p>
                            <ul className="list-disc list-inside text-xs space-y-0.5 text-rose-700">
                                {validationErrors.map((err, i) => (
                                    <li key={i}>{err}</li>
                                ))}
                            </ul>
                        </div>
                        <button
                            onClick={() => setValidationErrors([])}
                            className="ml-auto text-rose-600 hover:text-rose-800 p-1"
                            type="button"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </motion.div>
                )}

                {/* STEP 1: ROUTING & PRIORITY */}
                {currentStep === 1 && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        className="space-y-6"
                    >
                        <div className="bg-white border border-slate-200 rounded-3xl p-6 lg:p-8 shadow-sm space-y-6">
                            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                                <Store className="w-5 h-5 text-blue-600" />
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900">Branch Routing & Priority Matrix</h3>
                                    <p className="text-xs text-slate-500">Designate the facility requesting replenishment and level of operational urgency</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Branch */}
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                        Requesting Branch <span className="text-blue-600">*</span>
                                    </label>
                                    <select
                                        value={form.branchId}
                                        onChange={(e) => setForm({ ...form, branchId: e.target.value })}
                                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                                        required
                                    >
                                        <option value="">Select branch facility...</option>
                                        {branches.map((b: any) => (
                                            <option key={b.id} value={b.id}>
                                                {b.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Priority Selector */}
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                        Replenishment Urgency
                                    </label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {[
                                            { id: 'normal', label: 'Standard', color: 'border-slate-200 text-slate-700 hover:bg-slate-50' },
                                            { id: 'high', label: 'High Priority', color: 'border-amber-200 text-amber-800 hover:bg-amber-50' },
                                            { id: 'urgent', label: 'Critical / Urgent', color: 'border-rose-200 text-rose-800 hover:bg-rose-50' },
                                        ].map(p => (
                                            <button
                                                key={p.id}
                                                type="button"
                                                onClick={() => setPriority(p.id as any)}
                                                className={clsx(
                                                    'px-3 py-3 rounded-xl border text-xs font-bold transition-all',
                                                    priority === p.id
                                                        ? p.id === 'urgent'
                                                            ? 'bg-rose-50 border-rose-500 text-rose-800 shadow-sm ring-2 ring-rose-500/20'
                                                            : p.id === 'high'
                                                                ? 'bg-amber-50 border-amber-500 text-amber-800 shadow-sm ring-2 ring-amber-500/20'
                                                                : 'bg-blue-50 border-blue-500 text-blue-800 shadow-sm ring-2 ring-blue-500/20'
                                                        : p.color
                                                )}
                                            >
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Justification Notes */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                    Requisition Justification / Purpose
                                </label>
                                <textarea
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    rows={3}
                                    placeholder="Provide business justification (e.g. Expected weekend rush, sudden surge in local retail demand, stock replenishment)..."
                                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-slate-400 shadow-sm"
                                />
                            </div>

                            {/* Step 1 Footer */}
                            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                                <span className="text-xs text-slate-500">
                                    {canProceedToItems ? (
                                        <span className="text-emerald-600 flex items-center gap-1 font-medium">
                                            <CheckCircle2 className="w-4 h-4" /> Ready to select requested inventory items.
                                        </span>
                                    ) : (
                                        'Please select a requesting branch location.'
                                    )}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(2)}
                                    disabled={!canProceedToItems}
                                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-sm shadow-md shadow-blue-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                >
                                    <span>Proceed to Line Items</span>
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* STEP 2: REQUISITION ITEMS */}
                {currentStep === 2 && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        className="space-y-6"
                    >
                        {/* Low Stock AI Assistant Trigger Banner */}
                        {lowStockItems.length > 0 && (
                            <div className="p-5 rounded-3xl bg-gradient-to-r from-amber-50 via-amber-50/50 to-white border border-amber-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-2xl bg-amber-100 text-amber-800">
                                        <Zap className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-slate-900 flex items-center gap-2">
                                            <span>Low Stock Intelligence</span>
                                            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold border border-amber-200">
                                                {lowStockItems.length} items depleted
                                            </span>
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            Found items currently below safety reorder threshold in this branch.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleImportAllLowStock}
                                    className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs flex items-center gap-2 shadow-sm transition-all cursor-pointer"
                                >
                                    <Sparkles className="w-4 h-4" />
                                    <span>Import All {lowStockItems.length} Low Stock Items</span>
                                </button>
                            </div>
                        )}

                        <div className="bg-white border border-slate-200 rounded-3xl p-6 lg:p-8 shadow-sm space-y-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-slate-100">
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                        <Package className="w-5 h-5 text-blue-600" />
                                        <span>Requested Line Items</span>
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        Identify quantities needed for stock replenishment with estimated cost rates
                                    </p>
                                </div>
                                <button
                                    onClick={addItem}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 rounded-xl font-bold text-xs transition-all shadow-sm self-start sm:self-auto cursor-pointer"
                                    type="button"
                                >
                                    <Plus className="w-4 h-4" />
                                    <span>Add Item Row</span>
                                </button>
                            </div>

                            {/* Item Rows */}
                            <div className="space-y-4">
                                {items.map((item, idx) => {
                                    const qty = parseFloat(item.quantity || '0');
                                    const cost = parseFloat(item.estimatedUnitCost || '0');
                                    const lineTotal = (isNaN(qty) || isNaN(cost)) ? 0 : qty * cost;

                                    return (
                                        <div
                                            key={item.id}
                                            className={clsx(
                                                'p-5 rounded-2xl border transition-all space-y-4',
                                                item.isLowStockImported
                                                    ? 'bg-amber-50/50 border-amber-200'
                                                    : 'bg-slate-50/70 border-slate-200 hover:border-slate-300'
                                            )}
                                        >
                                            <div className="flex items-center justify-between text-xs text-slate-500">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold uppercase tracking-wider text-blue-600">
                                                        Item #{idx + 1}
                                                    </span>
                                                    {item.isLowStockImported && (
                                                        <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold">
                                                            Low Stock Trigger (Stock: {item.currentStock || '0'})
                                                        </span>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(idx)}
                                                    className="text-slate-400 hover:text-rose-600 transition-colors p-1"
                                                    title="Remove row"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                                                {/* Product Catalog */}
                                                <div className="md:col-span-4 relative">
                                                    <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                        Product Catalog *
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setActiveProductDropdownIndex(activeProductDropdownIndex === idx ? null : idx);
                                                            setProductSearch('');
                                                        }}
                                                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 hover:border-blue-400 rounded-xl text-left text-sm text-slate-800 flex items-center justify-between transition-all shadow-sm"
                                                    >
                                                        {item.productId ? (
                                                            <div className="truncate">
                                                                <p className="font-bold text-slate-900 text-xs truncate">{item.productName}</p>
                                                                {item.sku && <p className="text-[10px] text-slate-500 font-mono">SKU: {item.sku}</p>}
                                                            </div>
                                                        ) : (
                                                            <span className="text-slate-400 text-xs flex items-center gap-2">
                                                                <Search className="w-3.5 h-3.5" /> Select catalog product...
                                                            </span>
                                                        )}
                                                        <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                                                    </button>

                                                    <AnimatePresence>
                                                        {activeProductDropdownIndex === idx && (
                                                            <motion.div
                                                                initial={{ opacity: 0, y: -4 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                exit={{ opacity: 0, y: -4 }}
                                                                className="absolute top-full mt-2 w-full md:w-96 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 overflow-hidden left-0"
                                                            >
                                                                <div className="p-3 border-b border-slate-100 bg-slate-50/80">
                                                                    <div className="relative">
                                                                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Search product name or SKU..."
                                                                            value={productSearch}
                                                                            onChange={(e) => setProductSearch(e.target.value)}
                                                                            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:border-blue-500"
                                                                            autoFocus
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 p-1">
                                                                    {products.length === 0 ? (
                                                                        <div className="p-4 text-center text-xs text-slate-400">
                                                                            No matching products found.
                                                                        </div>
                                                                    ) : (
                                                                        products.map((p: any) => (
                                                                            <button
                                                                                key={p.id}
                                                                                type="button"
                                                                                onClick={() => selectProduct(idx, p)}
                                                                                className={clsx(
                                                                                    'w-full text-left p-3 rounded-xl transition-all hover:bg-slate-100 flex items-center justify-between',
                                                                                    item.productId === p.id && 'bg-blue-50 border border-blue-200'
                                                                                )}
                                                                            >
                                                                                <div className="truncate pr-2">
                                                                                    <p className="text-xs font-bold text-slate-900 truncate">{p.name}</p>
                                                                                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
                                                                                        {p.sku && <span className="font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{p.sku}</span>}
                                                                                        <span>Est. Cost: ₦{parseFloat(p.costPrice || '0').toLocaleString()}</span>
                                                                                    </div>
                                                                                </div>
                                                                                <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-2 py-1 rounded-lg shrink-0 border border-blue-200">
                                                                                    Select
                                                                                </span>
                                                                            </button>
                                                                        ))
                                                                    )}
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>

                                                {/* Quantity Requested */}
                                                <div className="md:col-span-2">
                                                    <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                        Requested Qty *
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0.001"
                                                        step="any"
                                                        value={item.quantity}
                                                        onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-right text-slate-800 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                                                        required
                                                    />
                                                </div>

                                                {/* UOM */}
                                                <div className="md:col-span-2">
                                                    <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                        UOM *
                                                    </label>
                                                    <select
                                                        value={item.uomId}
                                                        onChange={(e) => {
                                                            const chosen = item.availableUoms.find(u => u.id === e.target.value);
                                                            updateItem(idx, 'uomId', e.target.value);
                                                            if (chosen) updateItem(idx, 'uomName', chosen.name);
                                                        }}
                                                        disabled={!item.productId || item.availableUoms.length === 0}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:border-blue-500 disabled:opacity-40 transition-all shadow-sm"
                                                        required
                                                    >
                                                        <option value="">Select unit...</option>
                                                        {item.availableUoms.map(u => (
                                                            <option key={u.id} value={u.id}>
                                                                {u.name} {u.isBaseUom ? '(Base)' : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Estimated Unit Cost */}
                                                <div className="md:col-span-2">
                                                    <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                        Est. Cost (₦)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={item.estimatedUnitCost}
                                                        onChange={(e) => updateItem(idx, 'estimatedUnitCost', e.target.value)}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-right text-blue-600 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                                                    />
                                                </div>

                                                {/* Total Line Budget */}
                                                <div className="md:col-span-2 text-right pt-2 md:pt-0">
                                                    <span className="block text-[10px] font-semibold text-slate-500 uppercase">Estimated Budget</span>
                                                    <p className="text-sm font-bold text-slate-900 mt-1">
                                                        {formatNaira(lineTotal)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Line Notes Subrow */}
                                            <div className="pt-2 border-t border-slate-200/80">
                                                <input
                                                    type="text"
                                                    value={item.notes}
                                                    onChange={(e) => updateItem(idx, 'notes', e.target.value)}
                                                    placeholder="Item-specific notes (e.g. Specific brand preferred, immediate production requirement)..."
                                                    className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 shadow-sm"
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Summary Bar */}
                            <div className="pt-6 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6">
                                <div className="text-xs text-slate-500 space-y-1 w-full md:w-auto">
                                    <p className="flex items-center gap-2 text-slate-700">
                                        <Sparkles className="w-4 h-4 text-blue-600" />
                                        <span>Corporate Procurement Approval Workflow</span>
                                    </p>
                                    <p className="text-[11px] text-slate-500">
                                        Once approved by department head, this requisition converts into a vendor Purchase Order with 1 click.
                                    </p>
                                </div>

                                <div className="w-full md:w-80 bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3">
                                    <div className="flex items-center justify-between text-xs text-slate-500">
                                        <span>Total Units Requested</span>
                                        <span className="font-semibold text-slate-900">{totalRequestedQty.toLocaleString()} units</span>
                                    </div>
                                    <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
                                        <span className="text-sm font-bold text-slate-900">Estimated Budget</span>
                                        <span className="text-base font-black text-blue-600">{formatNaira(totalEstimatedBudget)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(1)}
                                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold text-sm transition-all shadow-sm"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    <span>Back to Department</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => createReqMutation.mutate()}
                                    disabled={!isReadyToSubmit || createReqMutation.isPending}
                                    className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all font-bold text-sm shadow-xl shadow-blue-600/25 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                                >
                                    {createReqMutation.isPending ? (
                                        <>
                                            <Spinner size="xs" variant="white" />
                                            <span>Submitting Requisition...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            <span>Submit Requisition ({formatNaira(totalEstimatedBudget)})</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
}